import clsx from "clsx";
import React from "react";
import {
  TableRow,
  TableRowProps,
  TableCell,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  ExerciseAssetDto,
  ExerciseAssetStatus,
  LibraryPrivacy,
} from "@growth-machine-llc/stridist-api-client";
import { WorkoutExercise } from "../workout/types";
import { ArrowForwardRounded } from "@mui/icons-material";
import { ThumbnailVideoBadge } from "./ThumbnailVideoBadge";
import { BodypartTypeLabels, MovementPatternTypeLabels } from "./constants";
import { ExercisesMenu } from "./ExercisesMenu";
import SeeMoreNotes from "../training-summary/SeeMoreNotes";
import { CategoryTypeLabel } from "./ManageLibraryExercise";
import { ExerciseCategoryBadge } from "./ExerciseCategoryBadge";
import { ExerciseTagBadge } from "./ExerciseTagBadge";
import { useUpdateLibraryExercise } from "./hooks/useUpdateLibraryExercise";
import { StackedCards } from "./StackedCards";
import PendingExerciseClientContainer from "./PendingExerciseClientContainer";
import ExercisePrivacyBadge from "./ExercisePrivacyBadge";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { TeamRole } from "../../constants";

const useStyles = makeStyles((theme) => ({
  root: {
    cursor: "pointer",
  },
  link: {
    textDecoration: "underline",
    color: theme.palette.text.primary,
    "&:hover": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2),
    },
  },
  value: {
    fontSize: 14,
    fontWeight: 500,
  },
  subText: {
    color: theme.palette.text.secondary,
    fontWeight: "normal",
    hyphens: "auto",
    hyphenateCharacter: "auto",
    wordBreak: "break-word",
  },
  tags: {
    "&.MuiBox-root": { m: 0 },
    width: "fit-content",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.8, 1.5),
    "& > *": {
      fontSize: 12,
    },
  },
  thumbnailList: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  category: {
    padding: theme.spacing(0.8, 1.5),
    "& > *": {
      fontSize: 12,
    },
  },
}));

const getExercisePrivacy = (exerciseInfo: {
  coachId?: number | null;
  isTeamShared?: boolean;
}): LibraryPrivacy => {
  if (!exerciseInfo.coachId) {
    return LibraryPrivacy.PUBLIC;
  } else if (exerciseInfo.isTeamShared) {
    return LibraryPrivacy.TEAM;
  } else {
    return LibraryPrivacy.PRIVATE;
  }
};

export interface TabsProps {
  isPendingTab: boolean;
  isPublishedTab: boolean;
  isArchivedTab: boolean;
}
export interface ExercisesTableRowProps extends TableRowProps, TabsProps {
  exerciseInfo: ExerciseAssetDto;
  onSelectedExercise?: (
    exerciseAsset: ExerciseAssetDto,
    publicTeamExercise?: ExerciseAssetDto,
  ) => void;
  isLoading?: boolean;
}

export const getColumnWidths = (
  isPendingTab: boolean,
  isPublishedTab: boolean,
  isArchivedTab?: boolean,
) => {
  if (isPendingTab) {
    return {
      thumbnail: "6%",
      title: "45%",
      suggestedBy: "39%",
      actions: "5%",
    };
  } else {
    return {
      thumbnail: "6%",
      title: isPublishedTab ? "25%" : "30%",
      movementPattern: "17%",
      bodypart: "18%",
      tags: "14%",
      images: "8%",
      type: isPublishedTab ? "13%" : null,
      image: isArchivedTab ? "13%" : null,
      actions: "5%",
    };
  }
};

export function ExercisesTableRow(props: ExercisesTableRowProps) {
  const {
    className,
    exerciseInfo,
    onSelectedExercise,
    isPendingTab,
    isPublishedTab,
    isArchivedTab,
    ...other
  } = props;
  const s = useStyles();

  const user = useCurrentUser();

  const exercise = JSON.parse(exerciseInfo.content) as WorkoutExercise;

  const exercisePrivacy = getExercisePrivacy(exerciseInfo);
  const columnWidths = getColumnWidths(isPendingTab, isPublishedTab);

  const { mutate: updateExercise } = useUpdateLibraryExercise();

  const handleSelectedExercise = React.useCallback(() => {
    if (onSelectedExercise) {
      onSelectedExercise(exerciseInfo);
    }
  }, [onSelectedExercise, exerciseInfo]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [movementPatterMenuOpen, setMovementPatterMenuOpen] =
    React.useState(false);
  const [bodypartMenuOpen, setBodypartMenuOpen] = React.useState(false);
  const [tagMenuOpen, setTagMenuOpen] = React.useState(false);

  const archived = exerciseInfo.archived;

  return (
    <TableRow
      className={clsx(s.root, className)}
      hover
      {...other}
      onClick={handleSelectedExercise}
    >
      <TableCell
        component="th"
        scope="row"
        sx={{ width: columnWidths.thumbnail }}
      >
        <ThumbnailVideoBadge
          size={58}
          border={exerciseInfo?.coachId === user?.id}
          video={exercise.video}
        />
      </TableCell>

      <TableCell component="th" scope="row" sx={{ width: columnWidths.title }}>
        <Box sx={{ width: "100%" }}>
          {exercise.title}
          <br />
          <Typography variant="caption" className={s.subText}>
            <SeeMoreNotes rows={2}>{exercise.instructions}</SeeMoreNotes>
          </Typography>
        </Box>
      </TableCell>

      {!isPendingTab && (
        <>
          <TableCell
            align="center"
            sx={{ width: columnWidths.movementPattern }}
          >
            <ExerciseCategoryBadge
              label={CategoryTypeLabel.MovementPattern}
              selectedItems={exerciseInfo.movementPatterns}
              labelMap={MovementPatternTypeLabels}
              open={movementPatterMenuOpen}
              setOpen={setMovementPatterMenuOpen}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              exercise={exerciseInfo}
              updateExercise={updateExercise}
              readonly={!exerciseInfo.coachId || archived}
            />
          </TableCell>

          <TableCell align="center" sx={{ width: columnWidths.bodypart }}>
            <ExerciseCategoryBadge
              label={CategoryTypeLabel.Bodypart}
              selectedItems={exerciseInfo.bodyparts}
              labelMap={BodypartTypeLabels}
              open={bodypartMenuOpen}
              setOpen={setBodypartMenuOpen}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              exercise={exerciseInfo}
              updateExercise={updateExercise}
              readonly={!exerciseInfo.coachId || archived}
              validateEmptySelection={true}
            />
          </TableCell>
        </>
      )}
      {isPendingTab ? (
        <TableCell align="left" sx={{ width: columnWidths.suggestedBy }}>
          {exerciseInfo.pendingAssetClientInfDto?.clientId ? (
            <PendingExerciseClientContainer
              client={exerciseInfo.pendingAssetClientInfDto}
            />
          ) : (
            "No info about a client"
          )}
        </TableCell>
      ) : (
        <TableCell align="center" sx={{ width: columnWidths.tags }}>
          <ExerciseTagBadge
            selectedTags={exerciseInfo.tags}
            open={tagMenuOpen}
            setOpen={setTagMenuOpen}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            exercise={exerciseInfo}
            readonly={!exerciseInfo.coachId || archived}
          />
        </TableCell>
      )}
      {isArchivedTab && (
        <TableCell
          align="center"
          sx={{
            width: columnWidths.images,
          }}
        >
          {exercise.images && exercise.images.length > 0 ? (
            <StackedCards
              items={exercise.images}
              limit={3}
              renderItem={(image) => (
                <ThumbnailVideoBadge video={image.url} border={false} />
              )}
            />
          ) : (
            "-"
          )}
        </TableCell>
      )}

      {isPublishedTab && (
        <TableCell
          align="center"
          sx={{
            width: columnWidths.type,
          }}
        >
          <Box display="flex" justifyContent="center">
            <ExercisePrivacyBadge
              privacy={exercisePrivacy}
              coach={exerciseInfo.coach}
            />
          </Box>
        </TableCell>
      )}
      <TableCell align="center" sx={{ width: columnWidths.actions }}>
        <Box
          display={"flex"}
          alignContent={"center"}
          flexDirection="column-reverse"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <IconButton
            children={<ArrowForwardRounded />}
            onClick={handleSelectedExercise}
            size="large"
            sx={{
              width: "min-content",
            }}
          />
          {(exerciseInfo.coachId === user?.id ||
            user?.teamRole === TeamRole.OWNER) && (
            <Box onClick={(e) => e.stopPropagation()}>
              <ExercisesMenu
                exerciseId={exerciseInfo.id}
                archived={exerciseInfo.archived}
                isTeamShared={exerciseInfo.isTeamShared}
                onExerciseSelected={handleSelectedExercise}
                pendingApproval={exerciseInfo?.isPendingApproval}
                exerciseNotification={exerciseInfo?.notification}
                exerciseCoachId={exerciseInfo?.coachId}
                bodyparts={exerciseInfo?.bodyparts}
              />
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}
